'use client';

import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useCallback,
  useState,
} from 'react';
import dynamic from 'next/dynamic';
import { useFetch } from './FetchProvider';
import {
  MultipleChoiceData,
  QuizConfidence,
  ItemResponseType,
  QuizResponseItemProps,
  QuizItem,
  XpLog,
  QuizTypes,
  ResponseDueData,
  FlashcardData,
  XpTypeMap,
} from '../types/quiz';
import { rateItem } from '../lib/frontend/quiz';
import { LocalQuizSettings } from '../types/page';
import { QuizSettings } from '../lib/shared/quiz/quizSetting';
import { setCookie, getCookie } from 'cookies-next';
import { QUIZ_CONFIDENCE_POPUP_KEY, xpTypeValues } from '../constants';
import useItemMetadata from '../hooks/useItemMetadata';
import { setFlagged } from '../lib/frontend/quiz/metadata';
import { reschedule, unschedule } from '../lib/frontend/quiz/spaced-repetition';
import { useSnackbarContext } from './SnackbarProvider';
import { useIntl } from 'react-intl';
import { QueryObserverBaseResult } from 'react-query';
import useModal from '../hooks/useModal';
import { TransformedAchievement } from '@osmosis/shared/types/achievement';
import { getAchievementsByType } from '../lib/frontend/achievements';
import { useUserContext } from './UserContext';
import { useLocalStorage } from '@osmosis/shared/hooks';

const AchievementInfoModal = dynamic(
  () => import('../components/achievements/AchievementInfoModal'),
  { ssr: false },
);

export type SubmitAnswerFunction = (
  confidence: QuizConfidence,
  isCorrect: boolean,
  response?: ItemResponseType,
  id?: number,
) => void;

type UpdateQuizSettings = (
  settingKey: QuizSettings,
  value: boolean | string,
) => void;

type GroupedQuizResponses = {
  correctHigh: number;
  correctMedium: number;
  correctLow: number;
  incorrectHigh: number;
  incorrectMedium: number;
  incorrectLow: number;
};

export type QuizContextProps = {
  setQuiz: Dispatch<SetStateAction<QuizResponseItemProps[]>>;
  nodeIDs: number[];
  bundleID?: string;
  isComplete: boolean;
  setIsComplete: (val: boolean) => void;
  isDailyPractice: boolean;
  currentResponseItem?: QuizResponseItemProps;
  currentItem?: QuizItem;
  currentItemMetadata?: ResponseDueData;
  refetchMetadata: QueryObserverBaseResult['refetch'];
  handleFlag: () => Promise<void>;
  handleReschedule: (minutes: number) => Promise<void>;
  handleUnschedule: () => Promise<void>;
  submitAnswer: SubmitAnswerFunction;
  itemStartTime: Date;
  correctItems: () => QuizResponseItemProps[];
  incorrectItems: () => QuizResponseItemProps[];
  currentIndex: number;
  totalItems: number;
  changeItem: (idxDelta: number) => void;
  showProgress: boolean;
  setShowProgress: (val: boolean) => void;
  showLab: boolean;
  setShowLab: (val: boolean) => void;
  highlighting: boolean;
  setHighlighting: (val: boolean) => void;
  fullQuizSize: boolean;
  setFullQuizSize: (val: boolean) => void;
  showRepetitionSettings: boolean;
  setShowRepetitionSettings: (val: boolean) => void;
  isAnswered: boolean;
  showFlashcardBack: boolean;
  setShowFlashcardBack: (val: boolean) => void;
  isShowExplanation: boolean;
  showElimination: boolean;
  setShowElimination: (val: boolean) => void;
  eliminationItems: number[];
  setEliminationItems: React.Dispatch<React.SetStateAction<number[]>>;
  isEliminated: boolean;
  setIsEliminated: (val: boolean) => void;
  handleRateQuestion: (rating: 1 | -1) => void;
  quiz: QuizResponseItemProps[];
  startQuiz: (items: QuizResponseItemProps[], idx?: number) => void;
  quizSettings: LocalQuizSettings;
  updateQuizSetting: UpdateQuizSettings;
  confidencePopupHidden: boolean;
  setConfidencePopupHidden: React.Dispatch<React.SetStateAction<boolean>>;
  quizType: QuizTypes | null;
  handleUpdateXp: (nodeID: number, type?: XpTypeMap) => void;
  xp: {
    total: number;
    correct: number;
    extra: number;
  };
  getGroupQuizResponses: (
    item: QuizResponseItemProps[],
  ) => GroupedQuizResponses;
};

export const initialState = {
  setQuiz: () => {
    /** */
  },
  currentItemMetadata: undefined,
  quizSettings: {},
  nodeIDs: [],
  isComplete: false,
  setIsComplete: () => {
    /** */
  },
  isDailyPractice: false,
  currentResponseItem: undefined,
  currentItem: undefined,
  quiz: [],
  submitAnswer: () => {
    /** */
  },
  itemStartTime: new Date(),
  correctItems: () => [],
  incorrectItems: () => [],
  currentIndex: 0,
  totalItems: 0,
  changeItem: () => {
    /** */
  },
  showProgress: false,
  setShowProgress: () => {
    /** */
  },
  showLab: false,
  setShowLab: () => {
    /** */
  },
  highlighting: false,
  setHighlighting: () => {
    /** */
  },
  fullQuizSize: false,
  setFullQuizSize: () => {
    /** */
  },
  showRepetitionSettings: false,
  setShowRepetitionSettings: () => {
    /** */
  },
  isAnswered: false,
  showFlashcardBack: false,
  setShowFlashcardBack: () => {
    /** */
  },
  isShowExplanation: false,
  showElimination: false,
  setShowElimination: () => {
    /** */
  },
  eliminationItems: [],
  setEliminationItems: () => {
    /** */
  },
  isEliminated: false,
  setIsEliminated: () => {
    /** */
  },
  toggleQuizBtnsOrder: () => {
    /** */
  },
  handleFlag: () => Promise.resolve(),
  handleReschedule: () => Promise.resolve(),
  handleUnschedule: () => Promise.resolve(),
  handleRateQuestion: () => {
    /** */
  },
  startQuiz: () => {
    /** */
  },
  updateQuizSetting: () => {
    /** */
  },
  confidencePopupHidden: false,
  setConfidencePopupHidden: () => {
    /** */
  },
  refetchMetadata: () => undefined as any,
  quizType: null,
  handleUpdateXp: () => {
    /** */
  },
  xp: {
    total: 0,
    correct: 0,
    extra: 0,
  },
  getGroupQuizResponses: () => ({
    correctHigh: 0,
    correctMedium: 0,
    correctLow: 0,
    incorrectHigh: 0,
    incorrectMedium: 0,
    incorrectLow: 0,
  }),
};

export const QuizContext = React.createContext<QuizContextProps>(initialState);

/**
 * The QuizProvider contains all state shared across a quiz.
 */
export const QuizProvider = (
  props: PropsWithChildren<{
    revalidateData?: () => void;
    quiz: QuizResponseItemProps[];
    bundleID?: string;
    isDailyPractice?: boolean;
    quizSettings?: LocalQuizSettings;
    quizType?: QuizTypes;
  }>,
): JSX.Element => {
  const {
    children,
    revalidateData,
    quiz: initialQuiz,
    bundleID,
    quizSettings: initialQuizSettings = {},
    isDailyPractice = false,
    quizType = QuizTypes.Questions,
  } = props;

  const { post } = useFetch();
  const { user } = useUserContext();

  const sortedInitialQuiz = [...initialQuiz].sort((a, b) => {
    const aLoggedOut = a.item.data?.loggedOut === 'true' ? 0 : 1;
    const bLoggedOut = b.item.data?.loggedOut === 'true' ? 0 : 1;
    return aLoggedOut - bLoggedOut;
  });

  const LOCAL_STORAGE_KEY = 'QuizData';
  const LOCAL_STORAGE_LOGGOUT_KEY = 'LOGGOUT_QUIZ';

  const [data, setData] = useLocalStorage(LOCAL_STORAGE_KEY, sortedInitialQuiz);
  const [loggoutQuiz, setLoggoutQuiz] = useLocalStorage(
    LOCAL_STORAGE_LOGGOUT_KEY,
    false,
  );

  const allNodeIDsEqual = () => {
    const ids1 = data.map((item) => item?.nodeID).sort();
    const ids2 = sortedInitialQuiz.map((item) => item?.nodeID).sort();
    if (ids1.length !== ids2.length) return false;

    return ids1.every((id, index) => id === ids2[index]);
  };

  const [quiz, setQuiz] = useState(sortedInitialQuiz);
  useEffect(() => {
    if (data && Array.isArray(data) && !user && allNodeIDsEqual()) {
      setQuiz(data);
      setLoggoutQuiz(true);
    }
    if (loggoutQuiz && user) {
      setCurrentIndex(2);
      localStorage.removeItem(LOCAL_STORAGE_LOGGOUT_KEY);
    }
  }, []);
  useEffect(() => {
    if (data && Array.isArray(data) && !user && allNodeIDsEqual()) {
      setQuiz(data);
    }

    if (data.some((item) => !!item.response) && user) {
      setQuiz(data);

      data
        .filter((item) => !!item.response && !!item.item?.nodeID)
        .forEach((item) => {
          const { answer, accuracy, confidence } = item.response!;
          const nodeID = item.item.nodeID;

          submitAnswer(confidence, accuracy, answer, nodeID);
        });
      localStorage.removeItem('QuizData');
    }
  }, [user, data]);

  const [quizSettings, setQuizSettings] =
    useState<LocalQuizSettings>(initialQuizSettings);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [itemStartTime, setItemStartTime] = useState(new Date());

  const [showProgress, setShowProgress] = useState(false);
  const [showLab, setShowLab] = useState(false);
  const [highlighting, setHighlighting] = useState(false);
  const [fullQuizSize, setFullQuizSize] = useState(false);
  const [showRepetitionSettings, setShowRepetitionSettings] = useState(false);
  const [showElimination, setShowElimination] = useState(false);
  const [eliminationItems, setEliminationItems] = useState<number[]>([]);
  const [isEliminated, setIsEliminated] = useState(false);
  const [xpLog, setXpLog] = useState<XpLog[]>([]);
  const [confidencePopupHidden, setConfidencePopupHidden] = useState(false);
  const [showFlashcardBack, setShowFlashcardBack] = useState(false);
  const achievementModal = useModal<TransformedAchievement[]>();

  const nodeIDs = quiz.map((i) => i.item.nodeID);
  const currentResponseItem = quiz[currentIndex];
  const currentItem = currentResponseItem?.item;
  const isAnswered = !!currentResponseItem?.response;

  const intl = useIntl();
  const { onAddSnackBar } = useSnackbarContext();

  useEffect(() => {
    if (quiz?.[currentIndex]?.response && !user) {
      setData(quiz);
    }
  }, [quiz?.[currentIndex]?.response]);

  const { metadata, refetch: refetchMetadata } = useItemMetadata(
    currentItem?.nodeID,
  );

  const updateQuizSetting: UpdateQuizSettings = (setting, value) => {
    setQuizSettings({ ...quizSettings, [setting]: value });
    setCookie(setting, value);
  };

  const handleFinishQuiz = () => {
    if (isDailyPractice) {
      //Submit xp if this quiz tracks XP
      post('/addResponseXp', { xpLog });
      // Log the streak
      post('/addStreakEntry', {
        type: 'daily_practice',
      });
    }
    setIsComplete(true);
  };

  /**
   * Changes the currentIndex based on the delta, setting isComplete as appropriate.
   */
  const changeItem = (delta: number): void => {
    // If a Lightbox image remains open when transitioning it will leave behind
    // a fixed position body that we need to remove.
    const body = document.querySelector('body');
    if (body) body.style.position = '';

    if (delta < 0) {
      setIsComplete(false);
    }

    if (currentIndex + delta >= quiz.length) {
      handleFinishQuiz();
      return;
    }
    if (currentIndex + delta < 0) return;
    setItemStartTime(new Date());
    setCurrentIndex(currentIndex + delta);
  };

  const startQuiz = (newItems: QuizResponseItemProps[], idx = 0) => {
    setIsComplete(false);
    setQuiz(newItems);
    setItemStartTime(new Date());
    setCurrentIndex(idx);
  };

  /**
   * Returns items answered correctly. With accuracy and confidence data.
   */
  const correctItems = () => quiz.filter((i) => i.response?.accuracy);

  /**
   * Returns items answered incorrectly. With accuracy and confidence data.
   */
  const incorrectItems = () =>
    quiz.filter((i) => i.response && !i.response?.accuracy);

  const handleRateQuestion = (newRating: 1 | -1) => {
    const newQuiz = [...quiz];
    newQuiz.splice(currentIndex, 1, {
      ...quiz[currentIndex],
      rating: newRating,
    });

    setQuiz(newQuiz);

    rateItem(post, currentItem.nodeID, newRating);
  };

  const handleUpdateXp = useCallback(
    (nodeID: number, type = XpTypeMap.CORRECT) => {
      const newXpLog: XpLog = {
        type,
        data: {
          nodeID,
        },
      };

      setXpLog([...xpLog, newXpLog]);
    },
    [xpLog],
  );

  const handleFlag = async () => {
    if (currentItem) {
      const flagged = !!metadata?.flagged;
      await setFlagged(post, [currentItem?.nodeID], flagged);
      refetchMetadata();

      onAddSnackBar({
        variant: 'dark',
        message: flagged
          ? intl.formatMessage({
              defaultMessage: 'Unflagged as priority for spaced repetition',
              id: 'quiz.quiz-settings-popup.priority-unflag.snackbar',
            })
          : intl.formatMessage({
              defaultMessage: 'Flagged as priority for spaced repetition',
              id: 'quiz.quiz-settings-popup.priority-flag.snackbar',
            }),
      });
    }
  };

  const handleReschedule = async (minutes: number) => {
    if (currentItem) {
      await reschedule(currentItem?.nodeID, minutes);
      refetchMetadata();

      onAddSnackBar({
        variant: 'dark',
        message: intl.formatMessage(
          {
            defaultMessage: `Item set to repeat in {minutes, plural,
              =0 {less than a minute}
              one {1 minute}
              other {{minutes} minutes}
              =1440 {1 day}
            }`,
            id: 'quiz.quiz-settings-popup.reschedule.snackbar',
          },
          { minutes },
        ),
      });
    }
  };

  const handleUnschedule = async () => {
    if (currentItem) {
      await unschedule(currentItem?.nodeID);
      refetchMetadata();

      onAddSnackBar({
        variant: 'dark',
        message: intl.formatMessage({
          defaultMessage: `Item removed from spaced repetition`,
          id: 'quiz.quiz-settings-popup.reschedule.snackbar.item_removed',
        }),
      });
    }
  };

  /**
   * Submits an answer, saving it in the backend and updating the context state.
   */
  const submitAnswer: SubmitAnswerFunction = useCallback(
    async (confidence, accuracy, itemResponse, id) => {
      const nodeID = nodeIDs[currentIndex];
      const etta = Math.round((+new Date() - +itemStartTime) / 1000);
      const timestamp = +Date.now();
      if (user) {
        post('/mobileRecordResponse2', {
          timestamp,
          resp: [
            {
              n: id || nodeID,
              acc: accuracy,
              conf: confidence,
              changed: false,
              etta,
              ettr: etta,
              ts: timestamp,
              tz: (-4 * new Date().getTimezoneOffset()) / 60,
              pf: 8,
              rate: -1,
            },
          ],
        }).then(async () => {
          const achievements = await getAchievementsByType(
            post,
            'questionsAnswered',
          );

          if (achievements.length) {
            achievementModal.show(achievements);
          }
        });
      }

      const response = {
        accuracy,
        confidence,
        timestamp,
        answer: itemResponse,
      };
      setQuiz((prevQuiz) => {
        const updatedQuiz = prevQuiz.map((item) => {
          if (item.item.nodeID === (id || nodeIDs[currentIndex])) {
            return {
              ...item,
              response,
            };
          }
          return item;
        });

        return updatedQuiz;
      });

      if (accuracy && isDailyPractice) {
        handleUpdateXp(nodeID);
      }
      setShowFlashcardBack(false);
      setShowLab(false);
    },
    [
      achievementModal,
      currentIndex,
      handleUpdateXp,
      isDailyPractice,
      itemStartTime,
      nodeIDs,
      post,
      quiz,
      revalidateData,
    ],
  );

  useEffect(() => {
    const confidencePopupValue = getCookie(QUIZ_CONFIDENCE_POPUP_KEY);
    if (typeof confidencePopupValue === 'boolean') {
      setConfidencePopupHidden(confidencePopupValue);
    }
  }, []);

  /**
   * To group correct and incorrect answers based on confidence level.
   */
  const getGroupQuizResponses = (items: QuizResponseItemProps[]) => {
    const result = {
      correctHigh: 0,
      correctMedium: 0,
      correctLow: 0,
      incorrectHigh: 0,
      incorrectMedium: 0,
      incorrectLow: 0,
    };

    for (const item of items) {
      const confidence = item.response?.confidence;
      const accuracy = item.response?.accuracy;

      if (accuracy) {
        if (confidence === 3) result.correctHigh++;
        else if (confidence === 2) result.correctMedium++;
        else result.correctLow++;
      } else {
        if (confidence === 3) result.incorrectHigh++;
        else if (confidence === 2) result.incorrectMedium++;
        else result.incorrectLow++;
      }
    }

    return result;
  };

  return (
    <QuizContext.Provider
      value={{
        /**
         * Resets the quiz to the new given quiz.
         */
        nodeIDs,
        bundleID,
        isComplete,
        setIsComplete,
        currentItem,
        totalItems: quiz.length,
        quiz,
        isDailyPractice,
        submitAnswer,
        itemStartTime,
        /**
         * Empty function for the initial default value of the context.
         */
        correctItems,
        /**
         * Empty function for the initial default value of the context.
         */
        incorrectItems,
        currentIndex,
        changeItem,
        showProgress,
        setShowProgress,
        showLab,
        setShowLab,
        highlighting,
        setHighlighting,
        fullQuizSize,
        setFullQuizSize,
        showRepetitionSettings,
        setShowRepetitionSettings,
        isAnswered,
        showFlashcardBack,
        setShowFlashcardBack,
        currentResponseItem,
        handleRateQuestion,
        isShowExplanation:
          (showFlashcardBack &&
            Boolean((currentItem.data as FlashcardData)?.mainExplanation)) ||
          (isAnswered &&
            !isComplete &&
            Boolean(
              (currentItem.data as MultipleChoiceData)?.mainExplanation ||
              (currentItem.data as MultipleChoiceData)?.keyTakeaway,
            )),
        eliminationItems,
        setEliminationItems,
        showElimination,
        setShowElimination,
        isEliminated,
        setIsEliminated,
        startQuiz,
        quizSettings,
        updateQuizSetting,

        /**
         * Resets the quiz to the given new quiz.
         */
        setQuiz,
        confidencePopupHidden,
        setConfidencePopupHidden,
        quizType,
        currentItemMetadata: metadata,
        refetchMetadata,
        handleFlag,
        handleReschedule,
        handleUnschedule,
        handleUpdateXp,
        xp: xpLog.reduce(
          (sum, current) => ({
            total: sum.total + xpTypeValues[current.type],
            correct:
              current.type === XpTypeMap.CORRECT
                ? sum.correct + xpTypeValues[current.type]
                : sum.correct,
            extra:
              current.type === XpTypeMap.EXTRA
                ? sum.extra + xpTypeValues[current.type]
                : sum.extra,
          }),
          { total: 0, correct: 0, extra: 0 },
        ),
        getGroupQuizResponses,
      }}
    >
      {children}
      {!!achievementModal.data && !!achievementModal.isVisible && (
        <AchievementInfoModal
          achievements={achievementModal.data}
          {...achievementModal}
        />
      )}
    </QuizContext.Provider>
  );
};

/**
 * Retrieves the current QuizContext.
 */
export const useQuizContext = (): QuizContextProps => useContext(QuizContext);
